import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Database,
  Receipt,
  BarChart3,
  Wrench,
  PiggyBank,
  ChevronDown,
  BookOpen,
} from 'lucide-react';

interface MenuItem {
  label: string;
  path: string;
  badge?: string;
  visible?: boolean;
}

interface MenuSection {
  name: string;
  icon: any;
  visible?: boolean;
  items: MenuItem[];
}

interface SidebarProps {
  appMode: 'WEB_PORTAL' | 'EXPO_SIMULATOR' | 'GATEWAY_TESTER' | 'EXPO_CODE';
  currentUser?: any;
}

export const Sidebar: React.FC<SidebarProps> = ({ appMode, currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const [isHovered, setIsHovered] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);

  // Only render sidebar if in WEB_PORTAL mode
  if (appMode !== 'WEB_PORTAL') return null;

  const userRole = currentUser?.levelUser || '';
  const isSuperadmin = userRole === 'SUPERADMIN';
  const isAdminKeuangan = userRole === 'ADMIN_KEUANGAN';
  const isKepalaSekolah = userRole === 'KEPALA_SEKOLAH';
  const isKasir = userRole === 'KASIR_LOKET';

  const rawMenuSections: MenuSection[] = [
    {
      name: 'Data Awal',
      icon: Database,
      visible: true,
      items: [
        { label: 'Kelas', path: '/kelas', visible: isSuperadmin || isAdminKeuangan || isKepalaSekolah },
        { label: 'Siswa & Virtual Account', path: '/siswa', visible: true },
        { label: 'Tahun Ajaran', path: '/tahun-ajaran', visible: isSuperadmin || isAdminKeuangan || isKepalaSekolah },
        { label: 'Akun Keuangan (COA)', path: '/akun', visible: isSuperadmin || isAdminKeuangan },
        { label: 'Pos Tagihan', path: '/pos-tagihan', visible: isSuperadmin || isAdminKeuangan },
        { label: 'Data Beasiswa/Potongan', path: '/data-beasiswa', badge: 'Baru', visible: isSuperadmin || isAdminKeuangan || isKepalaSekolah },
        { label: 'Tagihan Siswa', path: '/tagihan-siswa', visible: true },
        { label: 'Tagihan SPP Berkala', path: '/tagihan-spp-berkala', badge: 'Auto', visible: isSuperadmin || isAdminKeuangan },
        { label: 'Setting Tahun Ajaran', path: '/setting', visible: isSuperadmin || isAdminKeuangan },
      ].filter(it => it.visible),
    },
    {
      name: 'Transaksi',
      icon: Receipt,
      visible: true,
      items: [
        { label: 'Loket Pembayaran', path: '/loket', badge: 'Utama', visible: isSuperadmin || isAdminKeuangan || isKasir },
        { label: 'Cetak Bukti & Void', path: '/cetak-bukti', visible: isSuperadmin || isAdminKeuangan || isKasir },
        { label: 'Saldo Tabungan Siswa', path: '/saldo-siswa', visible: true },
        { label: 'Log Transaksi Siswa', path: '/transaksi-siswa', visible: true },
      ].filter(it => it.visible),
    },
    {
      name: 'Report',
      icon: BarChart3,
      visible: true,
      items: [
        { label: 'Laporan Rekap Tagihan', path: '/report/tagihan', visible: !isKasir },
        { label: 'Laporan Pembayaran Siswa', path: '/report/pembayaran', visible: true },
        { label: 'Laporan Tunggakan Siswa', path: '/report/tunggakan', badge: 'Penting', visible: !isKasir },
        { label: 'Laporan Penerimaan Kas', path: '/report/penerimaan', visible: true },
        { label: 'Jurnal Umum (G/L)', path: '/report/jurnal-umum', badge: 'Akuntansi', visible: isSuperadmin || isAdminKeuangan },
      ].filter(it => it.visible),
    },
    {
      name: 'Utility',
      icon: Wrench,
      visible: isSuperadmin || isAdminKeuangan || isKepalaSekolah,
      items: [
        { label: 'Proses Naik Kelas', path: '/utility/naik-kelas', visible: isSuperadmin || isAdminKeuangan },
        { label: 'Proses Kelulusan Siswa', path: '/utility/kelulusan', visible: isSuperadmin || isAdminKeuangan },
        { label: 'User Privilege (Hak Akses)', path: '/utility/user-privilege', visible: isSuperadmin },
        { label: 'Activity Log (Audit Trail)', path: '/utility/activity-log', visible: isSuperadmin || isAdminKeuangan || isKepalaSekolah },
        { label: 'Backup & Restore Database', path: '/utility/backup', visible: isSuperadmin },
      ].filter(it => it.visible),
    },
    {
      name: 'Tabungan',
      icon: PiggyBank,
      visible: true,
      items: [
        { label: 'Dashboard Tabungan', path: '/tabungan', badge: 'Baru', visible: true },
        { label: 'Data Mutasi Tabungan', path: '/tabungan/mutasi', visible: true },
        { label: 'Laporan Buku Tabungan', path: '/report/tabungan', visible: true },
      ].filter(it => it.visible),
    },
    {
      name: 'E-Wallet (Kantin)',
      icon: Database,
      visible: true,
      items: [
        { label: 'Command Center', path: '/ewallet', badge: 'Live', visible: true },
      ].filter(it => it.visible),
    },
    {
      name: 'Buku Panduan',
      icon: BookOpen,
      visible: true,
      items: [
        { label: 'Buku Panduan & Docs', path: '/panduan', badge: 'Docs', visible: true },
      ].filter(it => it.visible),
    },
  ];

  const menuSections = rawMenuSections.filter(sec => sec.visible && sec.items.length > 0);

  const toggleSection = (sectionName: string) => {
    // If opening a section while collapsed, optionally force expand the sidebar
    // but here it expands automatically on hover anyway.
    setOpenSection((prev) => (prev === sectionName ? null : sectionName));
  };

  const selectItem = (path: string) => {
    navigate(path);
  };

  return (
    <aside
      className={`hidden md:flex flex-col bg-sky-900 text-white transition-all duration-300 ease-in-out border-r border-sky-800 shrink-0 ${
        isHovered ? 'w-64 shadow-2xl z-40' : 'w-16'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setOpenSection(null); // auto close accordions when collapsing
      }}
    >
      {/* Brand Header */}
      <div className="h-14 flex items-center justify-center border-b border-sky-800 shrink-0">
        <div className="font-black text-white whitespace-nowrap overflow-hidden transition-all duration-300 flex items-center gap-2">
          {isHovered ? (
            <span className="text-xl">Menu Utama</span>
          ) : (
            <span className="text-xl">☰</span>
          )}
        </div>
      </div>

      {/* Menu Container */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar py-4 space-y-1">
        {/* Home */}
        <div className="px-2">
          <button
            onClick={() => selectItem('/')}
            className={`w-full flex items-center ${isHovered ? 'justify-start px-4' : 'justify-center'} py-2.5 rounded-lg transition-colors ${
              currentPath === '/' ? 'bg-sky-600 text-white' : 'text-sky-200 hover:bg-sky-800 hover:text-white'
            }`}
            title="Home"
          >
            <Home className="w-5 h-5 shrink-0" />
            <span
              className={`ml-3 text-sm font-semibold whitespace-nowrap transition-all duration-300 ${
                isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 hidden'
              }`}
            >
              Home
            </span>
          </button>
        </div>

        {/* Dynamic Sections */}
        {menuSections.map((section) => {
          const isSectionActive = section.items.some((it) => it.path === currentPath);
          const isOpen = openSection === section.name && isHovered;

          return (
            <div key={section.name} className="px-2">
              <button
                onClick={() => {
                  if (!isHovered) return; // Ignore clicks if collapsed
                  toggleSection(section.name);
                }}
                className={`w-full flex items-center py-2.5 rounded-lg transition-colors ${
                  isHovered ? 'justify-between px-4' : 'justify-center'
                } ${
                  isSectionActive
                    ? 'bg-sky-800 text-white'
                    : 'text-sky-200 hover:bg-sky-800 hover:text-white'
                }`}
                title={section.name}
              >
                <div className="flex items-center">
                  <section.icon className={`w-5 h-5 shrink-0 ${isSectionActive ? 'text-amber-300' : ''}`} />
                  <span
                    className={`ml-3 text-sm font-semibold whitespace-nowrap transition-all duration-300 ${
                      isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 hidden'
                    }`}
                  >
                    {section.name}
                  </span>
                </div>
                {isHovered && (
                  <ChevronDown
                    className={`w-4 h-4 shrink-0 transition-transform duration-200 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                )}
              </button>

              {/* Accordion Sub-items */}
              {isOpen && isHovered && (
                <div className="mt-1 mb-2 space-y-1 overflow-hidden animate-in slide-in-from-top-1 px-2">
                  {section.items.map((item) => {
                    const isSelected = currentPath === item.path;
                    return (
                      <button
                        key={item.path}
                        onClick={() => selectItem(item.path)}
                        className={`w-full flex items-center py-2 px-3 rounded-md transition-colors text-left ${
                          isSelected
                            ? 'bg-sky-700 text-white border-l-2 border-amber-400'
                            : 'text-sky-200 hover:bg-sky-800 hover:text-white'
                        }`}
                      >
                        <span className="text-[10px] shrink-0 mr-2 opacity-50">•</span>
                        <span className="text-xs truncate flex-1">{item.label}</span>
                        {item.badge && (
                          <span
                            className={`text-[9px] font-bold px-1.5 py-0.5 rounded ml-2 shrink-0 ${
                              item.badge === 'Utama'
                                ? 'bg-emerald-500/20 text-emerald-200'
                                : 'bg-rose-500/20 text-rose-200'
                            }`}
                          >
                            {item.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Footer in Sidebar */}
      <div className={`h-12 border-t border-sky-800 flex items-center justify-center ${isHovered ? 'px-4 justify-between' : ''}`}>
         {isHovered && <span className="text-xs text-sky-400 font-medium truncate">SDMT Ponorogo</span>}
         <Database className="w-4 h-4 text-sky-500 shrink-0" />
      </div>
    </aside>
  );
};
